import type { FC } from 'react'
import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'

import type { Document as RichTextDocument } from '@contentful/rich-text-types'

import { AppearanceProvider } from '@segment/matcha/foundations/AppearanceContext'

import Footer from '@segment/templates/Footer'
import Navigation from '@segment/templates/Navigation'

import GettingStarted from '@segment/templates/GettingStarted'

import { usePageQuery } from '@segment/api/routes/legal/queries'

import LayoutBase from '@segment/next-shared/layout'

import Hero from './components/Hero'

import Contents from './components/Contents'

import ogImageJpeg from './assets/OgHome.jpeg'

interface Content {
  url: string
  label: string
}

export interface LegalProps {
  pageSlug?: string
  contents?: Content[]
}

const LegalView: FC<LegalProps> = ({ pageSlug }) => {
  const theme = useTheme()
  const { data } = pageSlug ? usePageQuery({ args: [pageSlug] }) : { data: undefined }
  const pageTitle = data?.title || `Legal`

  return (
    <AppearanceProvider appearance="dark">
      <LayoutBase
        pageTitle="Twilio Segment | Legal"
        pageDescription="Twilio Segment is a customer data platform (CDP) that helps you collect, clean, and activate your customer data."
        pageOG={ogImageJpeg}
        themeColor={theme.palette.brand.darkAcai[700]}
      >
        <Navigation />
        <main>
          <Hero title={pageTitle} />
          <AppearanceProvider appearance="light">
            <Wrapper>
              <Contents data={data?.pageBody as RichTextDocument} />
            </Wrapper>
          </AppearanceProvider>
          <GettingStarted />
        </main>
        <Footer />
      </LayoutBase>
    </AppearanceProvider>
  )
}

export default LegalView

const Wrapper = styled.div`
  background: ${({ theme }) => theme.palette.neutrals.white[100]};
`
